// services/finanzasTrabajador.api.ts — ledger financiero del rol "trabajador"
// (empleado sin login). Espejo admin del API del aseador, pero:
//   - movimientos-only (el trabajador no hace aseos),
//   - sin endpoint `/me` (no tiene login → no hay auto-vista),
//   - todas las rutas admin-only.
// Reutiliza los tipos de movimiento del aseador (mismo shape de fila `gastos`).
import { apiFetch } from "./apiClient";
import type {
  MovimientoAseador,
  ConceptoAseador,
  MovimientoEstado,
} from "./finanzas.api";

const ERROR_MSG = "No se pudieron cargar las finanzas del trabajador";

/**
 * Un bucket (mes / semana / rango) del ledger del trabajador. Mismo shape que
 * `MesFinanzaAseador` pero SIN los campos de aseos (el trabajador no los tiene):
 * el total general = solo movimientos.
 */
export interface MesFinanzaTrabajador {
  year: number;
  month: number; // 1..12
  weekStart?: string; // YYYY-MM-DD (solo modo semanas)
  label: string;

  totalMovimientosRecibidos: number;
  totalMovimientosEmitidos: number;
  totalMovimientosNeto: number;
  totalMovimientosPagado: number;
  totalMovimientosPendiente: number;
  totalMovimientosSinEstado?: number;
  cantidadMovimientos: number;
  cantidadPendientes: number;
  movimientos: MovimientoAseador[];

  totalGeneralNeto: number;
  totalGeneralPagado: number;
  totalGeneralPendiente: number;
}

export interface FinanzasTrabajadorResponse {
  status: string;
  message: string;
  data: {
    rango: { from: string; to: string };
    meses: MesFinanzaTrabajador[];
    trabajador: { id: number; nombre: string };
  };
}

export const finanzasTrabajadorApi = {
  /**
   * Vista admin: ledger de un trabajador específico. Soporta rango por
   * `{ months }` (default 6) o `{ range: { from, to, label? } }` (panel único).
   */
  getFinanzasTrabajadorById: (
    trabajadorId: number,
    opts?: {
      months?: number;
      range?: { from: string; to: string; label?: string };
    },
  ): Promise<FinanzasTrabajadorResponse | null> => {
    const qs = new URLSearchParams();
    if (opts?.range?.from && opts?.range?.to) {
      qs.set("from", opts.range.from);
      qs.set("to", opts.range.to);
      if (opts.range.label) qs.set("label", opts.range.label);
    } else {
      qs.set("months", String(opts?.months ?? 6));
    }
    return apiFetch<FinanzasTrabajadorResponse>(
      `/finanzas/trabajador/${trabajadorId}?${qs.toString()}`,
      { errorMsg: ERROR_MSG },
    );
  },

  /** POST admin — registra un movimiento manual (nace pendiente). */
  crearMovimientoTrabajador: (
    trabajadorId: number,
    body: {
      monto: number;
      signo: 1 | -1;
      concepto: ConceptoAseador;
      descripcion: string;
      fecha: string; // YYYY-MM-DD
      rolAf?: "paga" | "recibe" | "intermediario";
      contraparte?:
        | { kind: "af" }
        | { kind: "propiedad"; propiedadId: number }
        | { kind: "externo"; nombreExterno?: string };
    },
  ): Promise<{ status: string; data: { id: number } } | null> =>
    apiFetch(`/finanzas/trabajador/${trabajadorId}/movimientos`, {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Movimiento registrado (pendiente de aprobación)",
      errorMsg: "No se pudo registrar el movimiento",
    }),

  /** PATCH admin — aprueba un pendiente (estado_pago='pagado'). */
  aprobarMovimientoTrabajador: (
    trabajadorId: number,
    movimientoId: number,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/trabajador/${trabajadorId}/movimientos/${movimientoId}/aprobar`,
      {
        method: "PATCH",
        successMsg: "Movimiento aprobado",
        errorMsg: "No se pudo aprobar el movimiento",
      },
    ),

  /** PATCH admin — deniega un movimiento (auditoría, no suma). */
  denegarMovimientoTrabajador: (
    trabajadorId: number,
    movimientoId: number,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/trabajador/${trabajadorId}/movimientos/${movimientoId}/denegar`,
      {
        method: "PATCH",
        successMsg: "Movimiento denegado",
        errorMsg: "No se pudo denegar el movimiento",
      },
    ),

  /** PATCH admin — revierte pagado/denegado → pendiente. */
  revertirMovimientoTrabajador: (
    trabajadorId: number,
    movimientoId: number,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/trabajador/${trabajadorId}/movimientos/${movimientoId}/revertir`,
      {
        method: "PATCH",
        successMsg: "Movimiento revertido a pendiente",
        errorMsg: "No se pudo revertir el movimiento",
      },
    ),

  /**
   * PATCH admin — fija el estado del pago de UN MES de un flujo regular
   * dirigido al trabajador (round 2026-07-27). Es el caso que motivó el round:
   * una suscripción o un arriendo que AF le paga todos los meses.
   *
   * La ocurrencia se identifica con (`gastoId`, `periodo`) porque el regular es
   * UNA fila que representa N pagos. Ver `marcarPagoRegularAseador`.
   */
  marcarPagoRegularTrabajador: (
    trabajadorId: number,
    gastoId: number,
    periodo: string,
    estado: MovimientoEstado,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/trabajador/${trabajadorId}/regulares/${gastoId}/${periodo}`,
      {
        method: "PATCH",
        body: JSON.stringify({ estado }),
        successMsg:
          estado === "pagado"
            ? "Pago del mes aprobado"
            : estado === "denegado"
              ? "Pago del mes denegado"
              : "Pago del mes vuelto a pendiente",
        errorMsg: "No se pudo actualizar el pago del mes",
      },
    ),

  /** PATCH admin — edita un movimiento (vuelve a pendiente si estaba cerrado). */
  editarMovimientoTrabajador: (
    trabajadorId: number,
    movimientoId: number,
    patch: {
      monto?: number;
      concepto?: string;
      descripcion?: string;
      fecha?: string;
      signo?: 1 | -1;
      rolAf?: "paga" | "recibe" | "intermediario";
    },
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/trabajador/${trabajadorId}/movimientos/${movimientoId}`,
      {
        method: "PATCH",
        body: JSON.stringify(patch),
        successMsg: "Movimiento editado",
        errorMsg: "No se pudo editar el movimiento",
      },
    ),

  /** DELETE admin — borra un movimiento pendiente/denegado (D-D). */
  borrarMovimientoTrabajador: (
    trabajadorId: number,
    movimientoId: number,
  ): Promise<{ status: string } | null> =>
    apiFetch(
      `/finanzas/trabajador/${trabajadorId}/movimientos/${movimientoId}`,
      {
        method: "DELETE",
        successMsg: "Movimiento borrado",
        errorMsg: "No se pudo borrar el movimiento",
      },
    ),

  /** POST admin — aprueba todos los pendientes del trabajador en un rango. */
  aprobarPendientesBulkTrabajador: (
    trabajadorId: number,
    body: { from: string; to: string },
  ): Promise<{
    status: string;
    data: { count: number; rango: { from: string; to: string } };
  } | null> =>
    apiFetch(
      `/finanzas/trabajador/${trabajadorId}/movimientos/aprobar-pendientes-bulk`,
      {
        method: "POST",
        body: JSON.stringify(body),
        errorMsg: "No se pudieron aprobar los pendientes",
      },
    ),
};
